import { Notification as MantineNotification, NotificationProps, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import { forwardRef, useMemo } from 'react';
import { getContrastingTextColor } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface ThemedNotificationProps extends NotificationProps {
  type?: NotificationType;
  gradient?: boolean;
  gradientVariant?: GradientVariant;
}

const Notification = forwardRef<HTMLDivElement, ThemedNotificationProps>(
  ({ type, gradient = false, gradientVariant = 'primary', icon, style, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradientConfig = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const gradientBg = `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const typeConfig = {
      success: { color: 'green', icon: <IconCheck size={20} /> },
      error: { color: 'red', icon: <IconX size={20} /> },
      warning: { color: 'yellow', icon: <IconAlertTriangle size={20} /> },
      info: { color: 'blue', icon: <IconInfoCircle size={20} /> },
    };

    const config = type ? typeConfig[type] : undefined;

    if (gradient) {
      return (
        <MantineNotification
          ref={ref}
          icon={icon || config?.icon}
          style={{
            backgroundImage: gradientBg,
            color: autoTextColor,
            border: 'none',
            ...accessibilityStyles,
            ...style,
          }}
          styles={{
            title: { color: autoTextColor, ...accessibilityStyles },
            description: { color: autoTextColor, opacity: 0.9, ...accessibilityStyles },
            closeButton: { color: autoTextColor, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineNotification
        ref={ref}
        icon={icon || config?.icon}
        color={config?.color}
        style={{ ...accessibilityStyles, ...style }}
        styles={{
          title: accessibilityStyles,
          description: accessibilityStyles,
        }}
        {...rest}
      />
    );
  }
);

Notification.displayName = 'Notification';

export default Notification;
